import _ from 'lodash';

import { ActionStepType } from 'constants/aiChat';

const LEGACY_ACTION_STEP_TYPE_MAP = Object.fromEntries(
  Object.entries(ActionStepType).map(([legacyKey, camelValue]) => [legacyKey, camelValue]),
);

const normalizeActionStepType = value => {
  if (_.isNil(value)) {
    return value;
  }

  return LEGACY_ACTION_STEP_TYPE_MAP[value] ?? value;
};

const normalizeContentProps = contentProps => {
  if (!_.isPlainObject(contentProps)) {
    return contentProps;
  }

  const hasSubStepType = !_.isNil(contentProps.subStepType);
  const hasNestedSteps = Array.isArray(contentProps.nestedSteps);

  if (!hasSubStepType && !hasNestedSteps) {
    return contentProps;
  }

  const normalizedSubStepType = hasSubStepType
    ? normalizeActionStepType(contentProps.subStepType)
    : contentProps.subStepType;

  const normalizedNestedSteps = hasNestedSteps
    ? contentProps.nestedSteps.map(nested =>
        _.isNil(nested?.contentProps)
          ? nested
          : { ...nested, contentProps: normalizeContentProps(nested.contentProps) },
      )
    : contentProps.nestedSteps;

  return {
    ...contentProps,
    ...(hasSubStepType ? { subStepType: normalizedSubStepType } : {}),
    ...(hasNestedSteps ? { nestedSteps: normalizedNestedSteps } : {}),
  };
};

const normalizeBlock = block => {
  if (!_.isPlainObject(block) || _.isNil(block.contentProps)) {
    return block;
  }

  return { ...block, contentProps: normalizeContentProps(block.contentProps) };
};

const getBlockId = block => block?.id ?? null;

const readSequenceNumber = block => {
  const raw = block?.contentProps?.sequenceNumber;

  if (_.isNil(raw)) {
    return null;
  }

  const asNumber = _.isNumber(raw) ? raw : Number(raw);

  return Number.isFinite(asNumber) ? asNumber : null;
};

const shouldDropOlderUpdate = (existingBlock, incomingBlock) => {
  const incomingSeq = readSequenceNumber(incomingBlock);
  const existingSeq = readSequenceNumber(existingBlock);

  if (_.isNil(incomingSeq) || _.isNil(existingSeq)) {
    return false;
  }

  return incomingSeq < existingSeq;
};

export const applyBlockAdded = (messageContentBlocks, payload) => {
  const messageId = payload?.messageId ?? null;
  const rawBlock = payload?.block ?? null;
  const block = normalizeBlock(rawBlock);
  const blockId = getBlockId(block);

  if (_.isNil(messageId) || _.isNil(block) || _.isNil(blockId)) {
    return messageContentBlocks;
  }

  const existing = messageContentBlocks[messageId] ?? [];
  const existingIndex = existing.findIndex(existingBlock => getBlockId(existingBlock) === blockId);

  if (existingIndex !== -1) {
    if (shouldDropOlderUpdate(existing[existingIndex], block)) {
      return messageContentBlocks;
    }

    const incomingSeq = readSequenceNumber(block);
    const existingSeq = readSequenceNumber(existing[existingIndex]);

    if (_.isNil(incomingSeq) || _.isNil(existingSeq)) {
      return messageContentBlocks;
    }

    const reconciled = [...existing];
    reconciled[existingIndex] = { ...existing[existingIndex], ...block };

    return {
      ...messageContentBlocks,
      [messageId]: reconciled,
    };
  }

  return {
    ...messageContentBlocks,
    [messageId]: [...existing, block],
  };
};

export const applyBlockUpdated = (messageContentBlocks, payload) => {
  const messageId = payload?.messageId ?? null;
  const rawBlock = payload?.block ?? null;
  const block = normalizeBlock(rawBlock);
  const blockId = getBlockId(block);

  if (_.isNil(messageId) || _.isNil(block) || _.isNil(blockId)) {
    return messageContentBlocks;
  }

  const existing = messageContentBlocks[messageId] ?? [];
  const index = existing.findIndex(existingBlock => getBlockId(existingBlock) === blockId);

  if (index === -1) {
    return {
      ...messageContentBlocks,
      [messageId]: [...existing, block],
    };
  }

  if (shouldDropOlderUpdate(existing[index], block)) {
    return messageContentBlocks;
  }

  const updated = [...existing];
  updated[index] = { ...existing[index], ...block };

  return {
    ...messageContentBlocks,
    [messageId]: updated,
  };
};

export const applyBlocksLoaded = (messageContentBlocks, payload) => {
  const messageId = payload?.messageId ?? null;
  const blocks = payload?.blocks ?? null;

  if (_.isNil(messageId) || !Array.isArray(blocks)) {
    return messageContentBlocks;
  }

  return {
    ...messageContentBlocks,
    [messageId]: blocks.map(normalizeBlock),
  };
};
